/*
 * AusMed PDP sticky tab nav
 * -------------------------
 * Progressive enhancement for the AusMed Product Details section.
 *
 * Responsibilities:
 * - Smooth-scroll to the target panel when a tab is clicked, accounting for
 *   the sticky header + the tab nav bar so the panel heading is not hidden.
 * - Scroll-spy: mark the tab matching the panel currently in view as active
 *   and keep that tab visible in its horizontal scroll container.
 * - On initial load, honour a `#ausmed-pdp-panel-*` hash by scrolling to the
 *   correct panel after layout has settled.
 *
 * Re-initialises on `shopify:section:load` so theme-editor edits re-wire
 * the handlers without a full page reload.
 */
(function () {
  'use strict';

  var SELECTOR_ROOT = '[data-ausmed-pdp-details-root]';
  var SELECTOR_TABS_CONTAINER = '[data-ausmed-pdp-tabs]';
  var SELECTOR_TAB = '[data-ausmed-pdp-tab]';
  var SELECTOR_PANEL = '[data-ausmed-pdp-panel]';

  /**
   * Returns a numeric pixel offset for the sticky tab bar. This is the
   * distance used when computing `scroll-margin-top` so the target panel
   * heading lands just below the tab nav rather than under it.
   */
  function getStickyOffset(root) {
    var tabBar = root.querySelector(SELECTOR_TABS_CONTAINER);
    var tabBarHeight = tabBar ? tabBar.getBoundingClientRect().height : 0;

    // Resolve the CSS custom property that tracks the header offset the
    // section was rendered with (section setting). Fall back to 96px.
    var raw = getComputedStyle(root).getPropertyValue('--ausmed-pdp-tabs-top');
    var headerOffset = parseInt(raw, 10);
    if (isNaN(headerOffset)) {
      headerOffset = 96;
    }

    return headerOffset + tabBarHeight + 16;
  }

  /**
   * Smooth-scrolls the page so `panel` appears below the sticky tab bar.
   */
  function scrollToPanel(root, panel) {
    if (!panel) return;

    var needsOpen = panel.tagName === 'DETAILS' && !panel.open;
    if (needsOpen) panel.open = true;

    // If opening changes layout, wait a frame so measurements are correct.
    var run = function () {
      var offset = getStickyOffset(root);
      var panelTop = panel.getBoundingClientRect().top + window.pageYOffset;
      var target = Math.max(panelTop - offset, 0);

      window.scrollTo({
        top: target,
        behavior: 'smooth'
      });
    };

    if (needsOpen) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(run);
      });
    } else {
      run();
    }
  }

  /**
   * Ensures the active tab is visible inside its horizontal scroller.
   */
  function ensureTabVisible(tabContainer, tab) {
    if (!tabContainer || !tab) return;
    var scroller = tabContainer.querySelector('.ausmed-pdp-tabs__scroll');
    if (!scroller) return;

    var tabRect = tab.getBoundingClientRect();
    var scrollerRect = scroller.getBoundingClientRect();

    if (tabRect.left < scrollerRect.left) {
      scroller.scrollBy({ left: tabRect.left - scrollerRect.left - 12, behavior: 'smooth' });
    } else if (tabRect.right > scrollerRect.right) {
      scroller.scrollBy({ left: tabRect.right - scrollerRect.right + 12, behavior: 'smooth' });
    }
  }

  /**
   * Sets `data-active="true"` on the tab whose panel id matches `id`.
   */
  function setActiveTab(root, id) {
    var tabContainer = root.querySelector(SELECTOR_TABS_CONTAINER);
    if (!tabContainer) return;

    var tabs = tabContainer.querySelectorAll(SELECTOR_TAB);
    var activeTab = null;

    tabs.forEach(function (tab) {
      var isActive = tab.getAttribute('data-ausmed-pdp-tab') === id;
      if (isActive) {
        tab.setAttribute('data-active', 'true');
        tab.setAttribute('aria-current', 'true');
        activeTab = tab;
      } else {
        tab.removeAttribute('data-active');
        tab.removeAttribute('aria-current');
      }
    });

    ensureTabVisible(tabContainer, activeTab);
  }

  /**
   * Initialise a single section root. Safe to call multiple times; handlers
   * are removed via AbortController before being re-attached.
   */
  function init(root) {
    if (!root) return;

    // Clean up any previous wiring to avoid duplicate handlers after
    // `shopify:section:load` events in the theme editor.
    if (root.__ausmedPdpTabsController) {
      root.__ausmedPdpTabsController.abort();
    }
    var controller = new AbortController();
    root.__ausmedPdpTabsController = controller;
    var signal = controller.signal;

    var tabContainer = root.querySelector(SELECTOR_TABS_CONTAINER);
    var tabs = tabContainer ? tabContainer.querySelectorAll(SELECTOR_TAB) : [];
    var panels = root.querySelectorAll(SELECTOR_PANEL);
    if (!tabs.length || !panels.length) return;

    // --- Click handling: smooth scroll + push shallow hash ---
    tabs.forEach(function (tab) {
      tab.addEventListener(
        'click',
        function (e) {
          var id = tab.getAttribute('data-ausmed-pdp-tab');
          var panel = root.querySelector('#' + CSS.escape(id));
          if (!panel) return;
          e.preventDefault();
          scrollToPanel(root, panel);
          setActiveTab(root, id);
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '#' + id);
          }
        },
        { signal: signal }
      );
    });

    // --- Scroll spy via IntersectionObserver ---
    // We watch a thin band near the top of the viewport (just under the
    // sticky bar) and activate whichever panel is passing through it.
    var lastActiveId = null;

    function updateActive() {
      var offset = getStickyOffset(root);
      // Find the panel whose top is closest to (but still above) the
      // sticky threshold. If no panel qualifies, use the first one below.
      var activePanel = null;
      var fallback = null;

      panels.forEach(function (panel) {
        var rect = panel.getBoundingClientRect();
        if (rect.top - offset <= 8) {
          activePanel = panel;
        } else if (!fallback) {
          fallback = panel;
        }
      });

      var chosen = activePanel || fallback || panels[0];
      if (!chosen) return;
      var id = chosen.getAttribute('data-ausmed-pdp-panel');
      if (id && id !== lastActiveId) {
        lastActiveId = id;
        setActiveTab(root, id);
      }
    }

    // Throttle with requestAnimationFrame to keep scroll smooth.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        updateActive();
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true, signal: signal });
    window.addEventListener('resize', onScroll, { signal: signal });
    updateActive();

    // --- Initial hash handling ---
    // Theme loads may paint the panel under the sticky bar; reposition once
    // layout has settled.
    function handleHash() {
      if (!window.location.hash || window.location.hash.indexOf('#ausmed-pdp-panel-') !== 0) return;
      var targetId = window.location.hash.slice(1);
      var targetPanel = root.querySelector('#' + CSS.escape(targetId));
      if (!targetPanel) return;
      scrollToPanel(root, targetPanel);
      setActiveTab(root, targetId);
    }

    // Run once at init, and on subsequent hash changes (e.g. user clicks
    // an anchor or the back/forward buttons).
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(handleHash);
    });
    window.addEventListener('hashchange', handleHash, { signal: signal });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialise on theme editor changes so new/removed blocks are picked up.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(SELECTOR_ROOT);
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector(SELECTOR_ROOT);
    if (root && root.__ausmedPdpTabsController) {
      root.__ausmedPdpTabsController.abort();
      root.__ausmedPdpTabsController = null;
    }
  });
})();
